import { isEmpty, isUndefined } from 'lodash-es';
import { MouseEventHandler, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { translate } from '../../utils/translations';
import logo from '../../assets/report_builder_logo.svg';
import {
  StyledErrorLayout,
  StyledErrorLayoutDialog,
  StyledErrorLayoutDialogAppName,
  StyledErrorLayoutDialogLogo,
  StyledErrorLayoutDialogTitle,
  StyledErrorLayoutDialogTitleIcon,
  StyledErrorLayoutDialogTitleText,
  StyledErrorLayoutDialogMessage,
  StyledErrorLayoutDialogMessageText,
  StyledErrorLayoutDialogButton,
  StyledErrorLayoutDialogBottomLink,
  StyledContentCopyIcon,
  StyledTraceparentIdWrapper,
} from './styles';

export type ErrorLayoutProps = {
  title: string;
  message: string;
  button?: { text: string; onClick: MouseEventHandler<HTMLButtonElement> };
  bottomLink?: { text: string; onClick: MouseEventHandler<HTMLButtonElement> };
  traceparentId?: string;
};

export function ErrorLayout({
  title,
  message,
  button,
  bottomLink,
  traceparentId,
}: ErrorLayoutProps) {
  const [copied, setCopied] = useState<boolean>(false);

  const copyTraceparentId = () => {
    if (!isEmpty(traceparentId)) {
      void navigator.clipboard.writeText(traceparentId as string).then(() => {
        setCopied(true);
      });
    }
  };

  const resetCopyStatus = () => {
    setCopied(false);
  };

  return (
    <StyledErrorLayout>
      <StyledErrorLayoutDialog>
        <StyledErrorLayoutDialogAppName>
          <StyledErrorLayoutDialogLogo>
            <img src={logo} alt="Report Builder" />
          </StyledErrorLayoutDialogLogo>
        </StyledErrorLayoutDialogAppName>
        <StyledErrorLayoutDialogTitle>
          <StyledErrorLayoutDialogTitleIcon />
          <StyledErrorLayoutDialogTitleText>
            {title}
          </StyledErrorLayoutDialogTitleText>
        </StyledErrorLayoutDialogTitle>
        <StyledErrorLayoutDialogMessage>
          <StyledErrorLayoutDialogMessageText>
            {message}
          </StyledErrorLayoutDialogMessageText>
        </StyledErrorLayoutDialogMessage>
        {!isEmpty(traceparentId) && (
          <Box mx={6} mb={5} data-testid="errorLayout-traceparentId-wrapper">
            <Typography align="center" component="p" mb={1} variant="body1">
              {translate('ERROR.ERROR_LAYOUT.TRACEPARENT_ID_DESCRIPTION')}
            </Typography>
            <Tooltip
              PopperProps={{ disablePortal: true }}
              title={translate(
                copied
                  ? 'ERROR.ERROR_LAYOUT.COPIED'
                  : 'ERROR.ERROR_LAYOUT.COPY_TO_CLIPBOARD'
              )}
            >
              <StyledTraceparentIdWrapper
                onClick={copyTraceparentId}
                onMouseLeave={resetCopyStatus}
              >
                <Typography component="p" variant="body1">
                  {traceparentId}
                  <StyledContentCopyIcon />
                </Typography>
              </StyledTraceparentIdWrapper>
            </Tooltip>
          </Box>
        )}
        {!isUndefined(button) && (
          <StyledErrorLayoutDialogButton
            color="primary"
            onClick={button.onClick}
            variant="contained"
            data-testid="errorLayout-button"
          >
            {button.text}
          </StyledErrorLayoutDialogButton>
        )}
        {!isUndefined(bottomLink) && (
          <StyledErrorLayoutDialogBottomLink
            color="primary"
            onClick={bottomLink.onClick}
            variant="text"
            data-testid="errorLayout-bottomLink"
          >
            {bottomLink.text}
          </StyledErrorLayoutDialogBottomLink>
        )}
      </StyledErrorLayoutDialog>
    </StyledErrorLayout>
  );
}
